import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useProductDetails } from "../useHook";
import ReactPlayer from "react-player";
import Loader, { ImageWithFallback } from "../../../Utils/loader";

function CategoriesProducts() {
  const location = useLocation();
  const { productId } = location.state || {};
  const { getProductDetails } = useProductDetails();
  const [activeTab, setActiveTab] = useState("shortDescription");
  const [productDetails, setProductDetails] = useState(null);
  // State for color and image selection
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    if (productId) {
      getProductDetails(productId, (data) => {
        setProductDetails(data);
        // Reset color and image selection if colors are available
        if (data && data.colors && data.colors.length > 0) {
          setSelectedColorIndex(0);
          setSelectedImageIndex(0);
        }
      });
    }
  }, [productId]);

  if (!productDetails) {
    return <Loader message="Loading product details..." />;
  }

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col p-4">
      <div className="bg-white shadow-md rounded-lg p-6 mb-6 flex-grow overflow-hidden">
        {/* Product Basic Info */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold text-gray-800 break-words">
              Product Name: {productDetails.name || "Unnamed Product"}
            </h1>
            <p className="text-xl font-semibold text-gray-800 mb-4">
              {productDetails.currency || ""} {productDetails.price || "N/A"}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>Status:</strong>{" "}
              <span
                className={`font-semibold ${productDetails.status ? "text-green-600" : "text-red-600"
                  }`}
              >
                {productDetails.status ? "Active" : "Inactive"}
              </span>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              <strong>Weight:</strong>{" "}
              {productDetails.weight ? productDetails.weight : "N/A"}
            </p>
            <p className="text-sm text-gray-600 mb-4 break-words">
              <strong>Dimensions:</strong>{" "}
              {productDetails.dimension ? productDetails.dimension : "N/A"}
            </p>
          </div>
        </div>

        {/* Color Selector Section */}
        {productDetails.colors && productDetails.colors.length > 0 && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Available Colors
            </h2>

            {/* Colors shown in a row as swatches */}
            <div className="flex space-x-4 mb-6 overflow-x-auto pb-2">
              {productDetails.colors.map((colorObj, index) => (
                <div
                  key={colorObj.id || index}
                  onClick={() => {
                    setSelectedColorIndex(index);
                    setSelectedImageIndex(0);
                  }}
                  className={`w-10 h-10 rounded-full cursor-pointer border-2 flex-shrink-0 ${selectedColorIndex === index
                    ? "border-blue-500"
                    : "border-transparent"
                    }`}
                  style={{ backgroundColor: colorObj.color || "transparent" }}
                  title={colorObj.color || "No Color"}
                ></div>
              ))}
            </div>

            {/* Display images for the selected color */}
            {productDetails.colors[selectedColorIndex] &&
              productDetails.colors[selectedColorIndex].image &&
              productDetails.colors[selectedColorIndex].image.length > 0 && (
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Main Image */}
                  <div className="flex-shrink-0">
                    <ImageWithFallback
                      src={
                        productDetails.colors[selectedColorIndex].image[
                        selectedImageIndex
                        ]
                      }
                      alt={`Main view for ${productDetails.colors[selectedColorIndex].color || "Color"
                        }`}
                      className="w-full max-w-[450px] h-[450px] object-cover rounded"
                    />
                  </div>

                  {/* Thumbnails */}
                  <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible">
                    {productDetails.colors[selectedColorIndex].image.map(
                      (imgUrl, idx) => (
                        <div key={idx} className="flex-shrink-0">
                          <ImageWithFallback
                            src={imgUrl}
                            alt={`Thumbnail ${idx}`}
                            className={`w-20 h-20 object-cover rounded cursor-pointer ${selectedImageIndex === idx
                              ? "border-2 border-blue-500"
                              : "border"
                              }`}
                            onClick={() => setSelectedImageIndex(idx)}
                          />
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
          </div>
        )}

        {/* Tabbed Description Section */}
        <div className="w-full min-w-0">
          <div className="flex border-b border-gray-300 overflow-x-auto">
            <button
              className={`flex-shrink-0 px-4 py-2 text-sm font-medium whitespace-nowrap ${activeTab === "shortDescription"
                ? "border-b-2 border-blue-500 text-blue-500"
                : "text-gray-700"
                }`}
              onClick={() => handleTabChange("shortDescription")}
            >
              Short Description
            </button>
            <button
              className={`flex-shrink-0 px-4 py-2 text-sm font-medium whitespace-nowrap ${activeTab === "description"
                ? "border-b-2 border-blue-500 text-blue-500"
                : "text-gray-700"
                }`}
              onClick={() => handleTabChange("description")}
            >
              Description
            </button>
            <button
              className={`flex-shrink-0 px-4 py-2 text-sm font-medium whitespace-nowrap ${activeTab === "additionalInformation"
                ? "border-b-2 border-blue-500 text-blue-500"
                : "text-gray-700"
                }`}
              onClick={() => handleTabChange("additionalInformation")}
            >
              Additional Info
            </button>
            <button
              className={`flex-shrink-0 px-4 py-2 text-sm font-medium whitespace-nowrap ${activeTab === "productVideo"
                ? "border-b-2 border-blue-500 text-blue-500"
                : "text-gray-700"
                }`}
              onClick={() => handleTabChange("productVideo")}
            >
              Product Video
            </button>
          </div>
          
          {/* Tab Content Container with proper overflow handling */}
          <div className="w-full min-w-0 mt-4">
            {activeTab === "shortDescription" && (
              <div className="text-sm text-gray-600 mb-4">
                <strong>Short Description:</strong>
                <div className="mt-2 break-all overflow-wrap-anywhere">
                  {productDetails.shortDescription ||
                    "No short description available"}
                </div>
              </div>
            )}
            {activeTab === "description" && (
              <div className="text-sm text-gray-600 mb-4">
                <strong>Description:</strong>
                <div
                  className="mt-2 break-all overflow-wrap-anywhere word-break-all max-w-full overflow-hidden"
                  style={{ 
                    wordWrap: 'break-word',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-all',
                    hyphens: 'auto'
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      productDetails.description || "No description available",
                  }}
                />
              </div>
            )}
            {activeTab === "additionalInformation" && (
              <div className="text-sm text-gray-600 mb-4">
                <strong>Additional Info:</strong>
                <div 
                  className="mt-2 break-all overflow-wrap-anywhere word-break-all max-w-full overflow-hidden"
                  style={{ 
                    wordWrap: 'break-word',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-all',
                    hyphens: 'auto'
                  }}
                >
                  {productDetails.additionalInformation ||
                    "No additional information available"}
                </div>
              </div>
            )}
            {/* {activeTab === "productVideo" && (
              <div className="text-sm text-gray-600 mb-4">
                <strong>Product Video:</strong>
                <div className="mt-2">
                  {productDetails.videoUrl ? (
                    <div className="w-full">
                      <ReactPlayer
                        url={productDetails.videoUrl}
                        controls
                        width="100%"
                        height="auto"
                        style={{ maxWidth: '100%' }}
                      />
                    </div>
                  ) : (
                    <p className="p-4 text-center text-gray-500">
                      No video available.
                    </p>
                  )}
                </div>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CategoriesProducts;